import { TabList, Tabs as JoyTabs, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { useEffect, useMemo, useState } from 'react';

import { ITab } from 'core/types';
import { Level } from 'theme/enums';

import { TabPanel } from './style';
import { Tab } from './Tab';

export interface ITabsProps {
  tabs: ITab[];
  ariaLabel: string;
  defaultTab?: string;
  onChange?: (value: string | number) => void;
  sx?: SxProps;
  hideTabBar?: boolean;
}

const resolveDefaultTab = (tabs: ITab[], defaultTab?: string) => {
  if (!defaultTab) {
    return { tab: tabs[0]?.value || 0, subTab: undefined };
  }

  if (tabs.some((t) => t.value === defaultTab)) {
    return { tab: defaultTab, subTab: undefined };
  }

  for (const tab of tabs) {
    if (tab.subTabs?.some((sub) => sub.value === defaultTab)) {
      return { tab: tab.value, subTab: { [tab.value]: defaultTab } };
    }
  }

  return { tab: defaultTab, subTab: undefined };
};

export const Tabs = ({
  tabs,
  ariaLabel,
  defaultTab,
  onChange,
  sx = {},
  hideTabBar = false,
}: ITabsProps) => {
  const resolvedDefault = resolveDefaultTab(tabs, defaultTab);

  const [selectedTab, setSelectedTab] = useState<string | number>(resolvedDefault.tab);
  const [selectedSubTab, setSelectedSubTab] = useState<Record<string | number, string | number>>(
    resolvedDefault.subTab || {}
  );

  // Stable token derived from the tabs shape (top-level values + sub-tab values).
  // Reruns the resolver when sub-tabs arrive after async load (deep-link case) but stays
  // stable across consumer rerenders that recompute the same shape, so user clicks aren't
  // snapped back to the default.
  const tabsToken = useMemo(
    () => tabs.map((t) => `${t.value}:${t.subTabs?.map((s) => s.value).join(',') ?? ''}`).join('|'),
    [tabs]
  );

  useEffect(() => {
    if (!defaultTab) {
      return;
    }

    const resolved = resolveDefaultTab(tabs, defaultTab);
    setSelectedTab(resolved.tab);

    if (resolved.subTab) {
      setSelectedSubTab(resolved.subTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, tabsToken]);

  const handleOnChange = (_: unknown, value: string | number) => {
    setSelectedTab(value);
    onChange?.(value);
  };

  return (
    <JoyTabs
      aria-label={ariaLabel}
      value={selectedTab}
      onChange={handleOnChange}
      sx={{ height: '100%', ...sx }}
    >
      {!hideTabBar && (
        <TabList>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              tab={{
                ...tab,
                value: tab.value || index,
              }}
              selectedSubTab={selectedSubTab}
              setSelectedSubTab={setSelectedSubTab}
            />
          ))}
        </TabList>
      )}

      {tabs.map((tab, index) => {
        if (tab.hide) {
          return null;
        }

        const panelSx = { ...(tab.style ?? {}), ...(hideTabBar && { paddingTop: 0 }) };

        if (tab?.subTabs) {
          const currentSubTabValue = selectedSubTab[tab.value || index];
          const currentSubTab =
            tab.subTabs.find((sub) => sub.value === currentSubTabValue) ||
            tab.subTabs.find((sub) => !sub.hide);

          return (
            <TabPanel key={index} value={tab.value} sx={panelSx}>
              {currentSubTab?.title && (
                <Typography level={Level.H4}>{currentSubTab.title}</Typography>
              )}

              {currentSubTab?.content}
            </TabPanel>
          );
        }

        return (
          <TabPanel key={index} value={tab.value || index} sx={panelSx}>
            {tab.title && <Typography level={Level.H4}>{tab.title}</Typography>}

            {tab.content}
          </TabPanel>
        );
      })}
    </JoyTabs>
  );
};
